import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import FAQSection from './FAQSection';
import './IndustriesPage.css';

const IndustriesPage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // const slideInRight = {
  //   hidden: { opacity: 0, x: 50 },
  //   visible: {
  //     opacity: 1,
  //     x: 0,
  //     transition: { duration: 0.8, ease: "easeOut" }
  //   }
  // };

  const keyFeatures = [
    {
      icon: "bi-gear-wide-connected",
      title: "Inventory & Supply Chain Management",
      description: "Real-time inventory tracking, demand planning, and supplier management for seamless operations.",
      color: "#FF7A00"
    },
    {
      icon: "bi-calendar-check",
      title: "Production Planning & Scheduling",
      description: "Advanced scheduling tools with resource optimization and capacity planning capabilities.",
      color: "#0066CC"
    },
    {
      icon: "bi-shield-check",
      title: "Quality Management & Fulfillment",
      description: "Comprehensive quality control processes and automated fulfillment workflows.",
      color: "#FF7A00"
    },
    {
      icon: "bi-calculator",
      title: "Financial Management & Cost Control",
      description: "Detailed cost accounting, budgeting, and financial reporting for manufacturing operations.",
      color: "#0066CC"
    },
    {
      icon: "bi-people-fill",
      title: "HR Workforce Intelligence & Manufacturing Analytics",
      description: "Workforce optimization and real-time manufacturing analytics for data-driven decisions.",
      color: "#FF7A00"
    },
    {
      icon: "bi-cloud-arrow-up",
      title: "Seamless Integration & Cloud Deployment",
      description: "Easy integration with existing systems and scalable cloud-based deployment.",
      color: "#0066CC"
    }
  ];

  const benefits = [
    {
      icon: "bi-graph-up-arrow",
      title: "Revenue Enhancement",
      description: "Optimize pricing strategies and improve sales performance with integrated analytics.",
      stats: "25% increase in revenue"
    },
    {
      icon: "bi-clock-history",
      title: "Process Manufacturers",
      description: "Streamline batch processing, formula management, and quality control procedures.",
      stats: "40% faster processing"
    },
    {
      icon: "bi-tools",
      title: "Industrial Equipment Manufacturers",
      description: "Manage complex assemblies, track warranties, and optimize maintenance schedules.",
      stats: "30% efficiency gain"
    },
    {
      icon: "bi-truck",
      title: "Automotive & Transport Manufacturers",
      description: "Handle complex supply chains, compliance requirements, and lean manufacturing processes.",
      stats: "50% cost reduction"
    },
    {
      icon: "bi-box-seam",
      title: "Food & Beverage Manufacturers",
      description: "Ensure food safety compliance, manage expiration dates, and optimize inventory turnover.",
      stats: "35% waste reduction"
    },
    {
      icon: "bi-cpu",
      title: "Electronics & High-Tech Manufacturers",
      description: "Manage rapid product lifecycles, component sourcing, and technology integrations.",
      stats: "60% faster time-to-market"
    }
  ];

  const processSteps = [
    {
      title: "Increased Production",
      description: "Streamline manufacturing operations and boost overall productivity",
      icon: "bi-speedometer2"
    },
    {
      title: "Enhanced Supply Chain",
      description: "Optimize supplier relationships and inventory management",
      icon: "bi-diagram-3"
    },
    {
      title: "Reporting & Visibility",
      description: "Real-time insights into manufacturing performance and KPIs",
      icon: "bi-graph-up"
    },
    {
      title: "Improved Cost Control",
      description: "Better financial management and cost optimization strategies",
      icon: "bi-piggy-bank"
    }
  ];

  return (
    <div className="industries-page">
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        {/* <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div> */}
        
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-gear me-2"></i>
                    MANUFACTURING INDUSTRY
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  NetSuite ERP for <span className="text-gradient">Manufacturing Industry</span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Transform your manufacturing operations with our comprehensive NetSuite ERP solution. 
                  Streamline production planning, optimize inventory management, and gain real-time visibility 
                  across your entire manufacturing process. From raw materials to finished goods, manage 
                  every aspect of your manufacturing business with precision and efficiency.
                </motion.p>
              </motion.div>
            </Col>
            
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: 45 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="hero-visual"
              >
                <div className="manufacturing-visual">
                  <motion.div 
                    className="gear-container"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-gear-wide-connected gear-icon"></i>
                  </motion.div>
                  <motion.div 
                    className="gear-small-1"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-gear-fill"></i>
                  </motion.div>
                  <motion.div 
                    className="gear-small-2"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                  >
                    <i className="bi-gear-fill"></i>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Key Features of NetSuite</h2>
              <p className="section-description">
                Comprehensive manufacturing capabilities designed for modern businesses
              </p>
            </motion.div>
            
            <Row className="g-4">
              {keyFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card className="feature-card h-100 border-0">
                      <Card.Body className="p-4">
                        <motion.div 
                          className="feature-icon mb-3"
                          style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <i className={`bi ${feature.icon}`}></i>
                        </motion.div>
                        <h5 className="feature-title mb-3">{feature.title}</h5>
                        <p className="feature-description">{feature.description}</p>
                        
                        <motion.div
                          className="feature-accent"
                          style={{ background: feature.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: "60px" }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title">Who Benefits from NetSuite for Manufacturing</h2>
                  <p className="section-description">
                    Discover how different manufacturing sectors leverage NetSuite to drive growth, 
                    improve efficiency, and achieve operational excellence.
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="h-100"
                  >
                    <Card className="benefit-card h-100 border-0">
                      <Card.Body className="p-4 text-center">
                        <motion.div 
                          className="benefit-icon mb-3"
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                        >
                          <i className={`bi ${benefit.icon}`}></i>
                        </motion.div>
                        <h5 className="benefit-title mb-3">{benefit.title}</h5>
                        <p className="benefit-description mb-3">{benefit.description}</p>
                        <Badge className="stats-badge">{benefit.stats}</Badge>
                      </Card.Body>
                      
                      <motion.div
                        className="card-accent"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Benefits Section */}
      <section className="process-section py-5">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Transform Your Manufacturing Operations</h2>
            </motion.div>
            
            <Row className="g-4">
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -15 }}
                    className="process-card text-center"
                  >
                    <motion.div 
                      className="process-icon mb-4"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <i className={`bi ${step.icon}`}></i>
                    </motion.div>
                    <h5 className="process-title mb-3">{step.title}</h5>
                    <p className="process-description">{step.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="cta-content">
              <motion.div
                className="cta-icon mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <i className="bi-rocket-takeoff"></i>
              </motion.div>
              <h2 className="cta-title mb-4">Ready to Transform Your Manufacturing?</h2>
              <p className="cta-description mb-4">
                Join hundreds of manufacturers who have revolutionized their operations with NetSuite ERP
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className="btn-primary-custom btn-lg px-5 py-3">
                    <i className="bi-chat-dots me-2"></i>
                    Get Started Today
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default IndustriesPage;